import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { UserManagementEditDialogsProps } from '../../types'

export function UserManagementEditDialogs({
  confirmOpen,
  setConfirmOpen,
  isActive,
  userEmail,
  user,
  finMgrRoleId,
  pendingSalesRole,
  setRemovingRoleId,
  setFinMgrRoleId,
  impersonateTarget,
  setImpersonateTarget,
  resetSalesRoleFlow,
  onSetTempPassword,
  onAccountToggle,
  onResetPassword,
  onRemoveRole,
  onFinanceManager,
  onSalesRoleUpdate,
  onImpersonate,
}: UserManagementEditDialogsProps) {
  const clearRemoveRole = () => {
    setRemovingRoleId(null)
    setConfirmOpen(null)
  }

  const clearFinMgr = () => {
    setFinMgrRoleId(null)
    setConfirmOpen(null)
  }

  const clearImpersonate = () => {
    setImpersonateTarget(null)
    setConfirmOpen(null)
  }

  const targetFinMgrRole = user?.roles?.find((r) => r.role_id === finMgrRoleId)
  const isFinMgrSet = targetFinMgrRole?.fin_mgr_attribute === 'YES'

  return (
    <>
      <ConfirmDialog
        isOpen={confirmOpen === 'temp-password'}
        title="Set Temp Password"
        message="Are you sure want to Set Temp Password?"
        primaryLabel="Confirm"
        onPrimary={() => void onSetTempPassword()}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirmOpen(null)}
        onDismiss={() => setConfirmOpen(null)}
        overlayDataTestId="um-edit-temp-password-overlay"
        primaryButtonDataTestId="um-edit-temp-password-confirm-btn"
        secondaryButtonDataTestId="um-edit-temp-password-cancel-btn"
        closeButtonDataTestId="um-edit-temp-password-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'account-toggle'}
        title={isActive ? 'Inactive User?' : 'Re-Activate User?'}
        message={
          isActive
            ? 'Are you sure want to Inactive this user?'
            : 'Are you sure want to Re-Activate this user?'
        }
        primaryLabel="Confirm"
        onPrimary={() => void onAccountToggle()}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirmOpen(null)}
        onDismiss={() => setConfirmOpen(null)}
        overlayDataTestId="um-edit-account-toggle-overlay"
        primaryButtonDataTestId="um-edit-account-toggle-confirm-btn"
        secondaryButtonDataTestId="um-edit-account-toggle-cancel-btn"
        closeButtonDataTestId="um-edit-account-toggle-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'reset-password'}
        title="Reset Password?"
        message={`Password Reset link will send to user email - ${userEmail ?? ''}`}
        primaryLabel="Confirm"
        onPrimary={() => void onResetPassword()}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirmOpen(null)}
        onDismiss={() => setConfirmOpen(null)}
        overlayDataTestId="um-edit-reset-password-overlay"
        primaryButtonDataTestId="um-edit-reset-password-confirm-btn"
        secondaryButtonDataTestId="um-edit-reset-password-cancel-btn"
        closeButtonDataTestId="um-edit-reset-password-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'remove-role-only-one'}
        title="Sorry, Unable To Remove User!"
        message="Please add a different role to the user, before removing their only role."
        primaryLabel="Ok"
        onPrimary={clearRemoveRole}
        secondaryLabel=""
        onSecondary={clearRemoveRole}
        onDismiss={clearRemoveRole}
        overlayDataTestId="um-edit-remove-role-only-one-overlay"
        primaryButtonDataTestId="um-edit-remove-role-only-one-ok-btn"
        closeButtonDataTestId="um-edit-remove-role-only-one-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'remove-role'}
        title="Remove Role"
        message="Delete User Role?"
        primaryLabel="Confirm"
        onPrimary={() => void onRemoveRole()}
        secondaryLabel="Cancel"
        onSecondary={clearRemoveRole}
        onDismiss={clearRemoveRole}
        overlayDataTestId="um-edit-remove-role-overlay"
        primaryButtonDataTestId="um-edit-remove-role-confirm-btn"
        secondaryButtonDataTestId="um-edit-remove-role-cancel-btn"
        closeButtonDataTestId="um-edit-remove-role-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'fin-mgr'}
        title="Finance Manager"
        message={`Are you sure want to ${isFinMgrSet ? 'remove' : 'set'} as Finance Manager`}
        primaryLabel="Confirm"
        onPrimary={() => void onFinanceManager()}
        secondaryLabel="Cancel"
        onSecondary={clearFinMgr}
        onDismiss={clearFinMgr}
        overlayDataTestId="um-edit-fin-mgr-overlay"
        primaryButtonDataTestId="um-edit-fin-mgr-confirm-btn"
        secondaryButtonDataTestId="um-edit-fin-mgr-cancel-btn"
        closeButtonDataTestId="um-edit-fin-mgr-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'sales-role'}
        title="Change Sales Role"
        message={`Do you want to set as ${pendingSalesRole === 'SALES_EXEC' ? 'Sales Exec' : 'Acct Mgr'}?`}
        primaryLabel="Yes"
        onPrimary={() => void onSalesRoleUpdate()}
        secondaryLabel="No"
        onSecondary={resetSalesRoleFlow}
        onDismiss={resetSalesRoleFlow}
        primaryFirst
        overlayDataTestId="um-edit-sales-role-overlay"
        primaryButtonDataTestId="um-edit-sales-role-yes-btn"
        secondaryButtonDataTestId="um-edit-sales-role-no-btn"
        closeButtonDataTestId="um-edit-sales-role-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'sales-role-assigned'}
        title="Sales Role Changed"
        message="Make sure you update the Sales Team(s) this person is assigned to."
        primaryLabel="OK"
        onPrimary={resetSalesRoleFlow}
        secondaryLabel="Close"
        onSecondary={resetSalesRoleFlow}
        onDismiss={resetSalesRoleFlow}
        overlayDataTestId="um-edit-sales-role-assigned-overlay"
        primaryButtonDataTestId="um-edit-sales-role-assigned-ok-btn"
        secondaryButtonDataTestId="um-edit-sales-role-assigned-close-action-btn"
        closeButtonDataTestId="um-edit-sales-role-assigned-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'sales-role-unassigned'}
        title="Sales Role Changed"
        message="Do you want to assign this person to a Sales Team?"
        primaryLabel="Yes"
        onPrimary={() => {
          window.open('/sales-team', '_blank')
          resetSalesRoleFlow()
        }}
        secondaryLabel="No"
        onSecondary={resetSalesRoleFlow}
        onDismiss={resetSalesRoleFlow}
        primaryFirst
        overlayDataTestId="um-edit-sales-role-unassigned-overlay"
        primaryButtonDataTestId="um-edit-sales-role-unassigned-yes-btn"
        secondaryButtonDataTestId="um-edit-sales-role-unassigned-no-btn"
        closeButtonDataTestId="um-edit-sales-role-unassigned-close-btn"
      />

      <ConfirmDialog
        isOpen={confirmOpen === 'impersonate'}
        title="Impersonate User"
        message="Are you sure you want to impersonate this user?"
        primaryLabel="Confirm"
        onPrimary={() => {
          setConfirmOpen(null)
          if (impersonateTarget != null) {
            void onImpersonate(impersonateTarget.roleId, impersonateTarget.roleName)
          }
        }}
        secondaryLabel="Cancel"
        onSecondary={clearImpersonate}
        onDismiss={clearImpersonate}
        overlayDataTestId="um-edit-impersonate-overlay"
        primaryButtonDataTestId="um-edit-impersonate-confirm-btn"
        secondaryButtonDataTestId="um-edit-impersonate-cancel-btn"
        closeButtonDataTestId="um-edit-impersonate-close-btn"
      />
    </>
  )
}
